import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {HttpUtilsService, QueryParamsModel, QueryResultsModel} from '../_base/crud';
import {FileModel} from '../models/file.model';
import {first, map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import streamSaver from 'streamsaver';
import { LibraryFileModel } from '../models/library-file.model';
import { IconData } from '../models/user.model';
import { MobileAppService } from './mobile-app.service';

const API_FILES_URL = `${environment.pathToAPI}/api/v1/files`;

@Injectable()
export class FilesService {
    constructor(
        private http: HttpClient,
        private mobileAppService: MobileAppService,
        private httpUtils: HttpUtilsService) {
    }

    createFile(file: FileModel): Observable<FileModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<FileModel>(API_FILES_URL, file, {headers: httpHeaders});
    }

    getFileById(fileId: number, token: string | null = null): Observable<FileModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders(token);
        return this.http.get<FileModel>(API_FILES_URL + `/${fileId}`, {headers: httpHeaders});
    }

    findFiles(queryParams: QueryParamsModel<FileModel>, token: string | null = null): Observable<QueryResultsModel<FileModel>> {
        console.log('FilesService.findFiles!');
        const httpHeaders = this.httpUtils.getHTTPHeaders(token);
        return this.http.post(
            API_FILES_URL + '/find',
            queryParams,
            {headers: httpHeaders}
        ).pipe(map<any, QueryResultsModel<FileModel>>((value) => {
            return new QueryResultsModel(value.response, value.totalCount, value.isReadStatus, '');
        }));
    }

    updateFile(file: FileModel): Observable<FileModel> {
        const httpHeader = this.httpUtils.getHTTPHeaders();
        return this.http.put<FileModel>(API_FILES_URL + `/${file.id}`, file, {headers: httpHeader});
    }


    updateStatusForFile(files: FileModel[], status: number): Observable<any> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const body = {
            filesForUpdate: files,
            newStatus: status
        };
        const url = API_FILES_URL + '/updateStatus';
        return this.http.put(url, body, {headers: httpHeaders});
    }

    deleteFile(fileId: number): Observable<FileModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const url = `${API_FILES_URL}/${fileId}`;
        return this.http.delete<FileModel>(url, {headers: httpHeaders});
    }

    requestDownload(fileId: number, fileType: string | null, token: string | null | undefined = null) {
        const httpHeaders = this.httpUtils.getHTTPHeaders(token);
        let query = '';
        if (fileType !== null ) {
            query = '?file_type=' + fileType;
        }
        return this.http.get(
            API_FILES_URL + `/${fileId}/request_download${query}`,
            {headers: httpHeaders}).pipe(map<any, string>((value) => {
            return API_FILES_URL + `/download/${value.key}`;
        }));
    }

    isImage(file: FileModel | undefined) {
        if (!file || !file.filePath) {
            return;
        }
        const filePath = file.filePath.toLowerCase()
        return filePath.endsWith('.jpg') ||
            filePath.endsWith('.jpeg') ||
            filePath.endsWith('.webp') ||
            filePath.endsWith('.png') ||
            filePath.endsWith('.gif');
    }

    canOpenFile(file: FileModel | LibraryFileModel | undefined) {
        if (!file || !file.filePath) {
            return;
        }
        const filePath = file.filePath.toLowerCase()
        return filePath.endsWith('.jpg') ||
            filePath.endsWith('.jpeg') ||
            filePath.endsWith('.png') ||
            filePath.endsWith('.fbx') ||
            filePath.endsWith('.3ds') ||
            filePath.endsWith('.obj') ||
            filePath.endsWith('.dxf');
    }

    downloadFile(file: FileModel) {
        this.requestDownload(file.id, null).pipe(first()).subscribe((value: string) => {
            const fileStream = streamSaver.createWriteStream(file.fileName);
            const writer = fileStream.getWriter();
            fetch(value).then(res => {
                const reader = res.body?.getReader();
                if (!reader) {
                    console.log("can not download file:" + file.fileName);
                    return;
                }
                const pump: () => Promise<void> = () => reader.read().then(result => result.done ? writer.close() : writer.write(result.value).then(pump));
                pump().then(() => console.log('file downloaded')).catch((error) => console.error("download failed: " + error));
            });
        });
    }

    formatTagData(file: FileModel | undefined) {
        if (!file || !file.tags) {
            return [];
        }
        let result = file.tags.split('|').join('');
        // deleting last comma
        result = result.slice(0, -1);
        return result.split(',');
    }

    resolvedPicFromPath(path: string | undefined | null, saved_to: string | undefined | null, pic_thumbnail: string | undefined | null) {
        if (!path) {
            return "/assets/media/users/default.jpg";
        }
        // システムが用意したアイコンはブラウザアプリ側のassetとして提供されているの前提なので、"/"で始まる。
        if (path.startsWith('/')) {
            return path;
        }
        if (saved_to === "aws") {
            return pic_thumbnail || "/assets/media/users/default.jpg";
        }
        
        // アップロードは基本AWSへ保存しないとセキュリティ上のリスクがある。（ユーザーのアイコンのURLが漏れた時、ユーザーのプライバシー情報が漏れてしまう）
        return environment.pathToUploads + path.replace('.', '_thumbnail.');
    }

    resolvedPic(user: IconData | undefined | null): string {
        if (!user || !user.pic) {
            return "/assets/media/users/default.jpg";
        }
        // システムが用意したアイコンはブラウザアプリ側のassetとして提供されているの前提なので、"/"で始まる。
        if (user.pic.startsWith('/')) {
            return user.pic;
        }
        if (user.saved_to === "aws") {
            return user.pic_thumbnail || "/assets/media/users/default.jpg";
        }
        // アップロードした画像。アイコンはAWSへは保存しない設計のようだ。
        // TODO これはセキュリティ上のリスクがあるのでは？（ユーザーのアイコンのURLが漏れた時、ユーザーのプライバシー情報が漏れてしまう）
        return environment.pathToUploads + user.pic.replace('.', '_thumbnail.');
    }

    fetchFileAndSave(filePath: string, filename: string) {
        if (this.mobileAppService.os() === undefined) {
            fetch(filePath).then(response => {
                const fileStream = streamSaver.createWriteStream(filename);
                const writer = fileStream.getWriter();
                const reader = response.body?.getReader();
                if (!reader) {
                    console.error("can not download file:" + filename);
                    return;
                }
                const pump: () => Promise<void> = () => reader.read().then(result => result.done ? writer.close() : writer.write(result.value).then(pump));
                pump().then(() => console.log('pump')).catch((error) => console.error(error));
            });
        } else {
            this.mobileAppService.fileDownload(filePath, filename);
        }
    }
}
